import usePrevious from "@insite/client-framework/Common/Hooks/usePrevious";
import { AnchorLink, useAnchorContext } from "@toolkit/Framework/Components/AnchorContext";
import { useEffect } from "react";

const getId = (anchor?: string, label?: string) => `${anchor}.${label}`;

const getIndex = (links: AnchorLink[], id?: string) => {
    return links.findIndex(x => x.id === id);
};

const addLink = (links: AnchorLink[], newLink: AnchorLink) => {
    return [...links, newLink];
};

const removeLink = (links: AnchorLink[], index: number) => {
    if (index !== -1) {
        const _links = [...links];
        _links.splice(index, 1);

        return _links;
    }

    return links;
};

const replaceLink = (links: AnchorLink[], index: number, newLink: AnchorLink) => {
    return links.map((link, mapIndex) => (mapIndex === index ? newLink : link));
};

const has = (index: number) => {
    return index !== -1;
};

export default (anchor: string, label: string) => {
    const { setLinks } = useAnchorContext();
    const previousAnchor = usePrevious(anchor);
    const previousLabel = usePrevious(label);

    useEffect(() => {
        if (setLinks) {
            setLinks(links => {
                if (!previousAnchor && !anchor) {
                    return links;
                }

                const previousId = getId(previousAnchor, previousLabel);
                const previousIndex = getIndex(links, previousId);

                if (has(previousIndex) && !anchor) {
                    return removeLink(links, previousIndex);
                }

                const id = getId(anchor, label);
                const currentIndex = getIndex(links, id);
                const link: AnchorLink = { id, label, anchor };

                if (has(previousIndex) && has(currentIndex)) {
                    return replaceLink(removeLink(links, previousIndex), currentIndex, link);
                }

                if (has(previousIndex) && !has(currentIndex)) {
                    return replaceLink(links, previousIndex, link);
                }

                if (!has(previousIndex) && has(currentIndex)) {
                    return replaceLink(links, currentIndex, link);
                }

                return addLink(links, link);
            });
        }
    }, [anchor, label]);

    useEffect(() => {
        if (setLinks) {
            return () => setLinks(links => removeLink(links, getIndex(links, getId(anchor, label))));
        }
    }, []);
};
